import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Card, Chip, CircularProgress } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EmailIcon from "@mui/icons-material/Email";
import ReportIcon from "@mui/icons-material/Report";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TimerIcon from "@mui/icons-material/Timer";
import { motion } from "framer-motion";
import api from "../../../src/utils/api";

/* ── Status chip colours ─────────────────────────────────────────────────── */
const STATUS_COLORS = {
  open:        { bg: "rgba(234,88,12,0.12)",   text: "#ea580c" },
  in_progress: { bg: "rgba(37,99,235,0.12)",   text: "#2563eb" },
  pending:     { bg: "rgba(217,119,6,0.12)",   text: "#b45309" },
  resolved:    { bg: "rgba(22,163,74,0.12)",   text: "#16a34a" },
  closed:      { bg: "rgba(107,114,128,0.12)", text: "#6b7280" },
  cancelled:   { bg: "rgba(220,38,38,0.12)",   text: "#dc2626" },
  active:      { bg: "rgba(234,88,12,0.12)",   text: "#ea580c" },
  corrected:   { bg: "rgba(22,163,74,0.12)",   text: "#16a34a" },
  appealed:    { bg: "rgba(139,92,246,0.12)",  text: "#7c3aed" },
};

const statusChipSx = (status = "") => {
  const key   = status.toLowerCase().replace(/\s+/g, "_");
  const style = STATUS_COLORS[key] || { bg: "rgba(47,99,173,0.1)", text: "#2f63ad" };
  return {
    height: 20,
    borderRadius: "6px",
    fontWeight: 700,
    fontSize: "0.67rem",
    backgroundColor: style.bg,
    color: style.text,
    "& .MuiChip-label": { px: "6px" },
  };
};

/* ── Timeline type config ────────────────────────────────────────────────── */
const timelineTypeConfig = {
  violation: {
    color: "#1E88E5",
    icon:  ReportIcon,
    label: "Violation Created/Updated",
  },
  status: {
    color: "#3949AB",
    icon:  SyncAltIcon,
    label: "Status Change",
  },
  cure_reset: {
    color: "#1565C0",
    icon:  TimerIcon,
    label: "Cure Period Reset",
  },
  comment: {
    color: "#43A047",
    icon:  ChatBubbleIcon,
    label: "Comment",
  },
  event: {
    color: "#8E24AA",
    icon:  EventNoteIcon,
    label: "Event",
  },
  email: {
    color: "#FB8C00",
    icon:  EmailIcon,
    label: "Email",
  },
};

const ViolationsTimeline = ({ violation_id }) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/violationsTimeline/${violation_id}`);
        setTimelineData(response.data);
      } catch (err) {
        console.error("Error fetching timeline data:", err);
        setError("Failed to load timeline data.");
      } finally {
        setLoading(false);
      }
    };
    if (violation_id) fetchTimeline();
  }, [violation_id]);

  const combinedTimeline = useMemo(() => {
    if (!timelineData?.violation) return [];

    const {
      violation,
      comments      = [],
      events        = [],
      emails        = [],
      statusHistory = [],
      cureDaysResets = [],
    } = timelineData;

    const timeline = [
      {
        type:  "violation",
        title: "Violation Created",
        time:  violation.created_at,
        by:    `${violation.created_by_name || "Unknown"} ${violation.created_by_lastname || ""}`.trim(),
      },

      ...statusHistory.map(({ old_status, new_status, changed_at, changed_by_name, changed_by_lastname, comment_text }) => ({
        type:       "status",
        title:      `Status changed from "${old_status || "N/A"}" → "${new_status || "N/A"}"`,
        time:       changed_at || new Date().toISOString(),
        by:         `${changed_by_name || "System"} ${changed_by_lastname || ""}`.trim(),
        comment:    comment_text || "",
        old_status,
        new_status,
      })),

      ...comments.map(({ comment_text, sent_at, user_name }) => ({
        type:  "comment",
        title: comment_text,
        time:  sent_at || new Date().toISOString(),
        by:    user_name || "Unknown User",
      })),

      ...events.map(({ title, created_at }) => ({
        type:  "event",
        title,
        time:  created_at || new Date().toISOString(),
      })),

      ...emails.map(({ subject, sent_at, email_address }) => ({
        type:  "email",
        title: subject,
        time:  sent_at || new Date().toISOString(),
        by:    email_address || "System Notification",
      })),

      ...cureDaysResets.map(({ restart_number, days_to_cure, recorded_at, recorded_by_name, recorded_by_lastname, notes }) => ({
        type:         "cure_reset",
        title:        restart_number === 1 ? "Cure Period Started" : `Cure Period Restarted (#${restart_number})`,
        time:         recorded_at || new Date().toISOString(),
        by:           `${recorded_by_name || "System"} ${recorded_by_lastname || ""}`.trim(),
        days_to_cure,
        notes:        notes || "",
        restart_number,
      })),

      ...(violation.updated_at
        ? [{ type: "violation", title: "Violation Updated", time: violation.updated_at }]
        : []),
    ];

    return timeline.sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [timelineData]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error" sx={{ textAlign: "center" }}>{error}</Typography>;
  }

  if (combinedTimeline.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 2.5, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No timeline activity found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
          Timeline activity will appear here as the violation is updated.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: "#223142" }}>
        Violations Timeline
      </Typography>

      <Box
        sx={{
          position: "relative",
          pl: { xs: 2, sm: 2.5 },
          "&::before": {
            content: '""',
            position: "absolute",
            left: { xs: "9px", sm: "11px" },
            top: 8,
            bottom: 8,
            width: "2px",
            backgroundColor: "rgba(47,99,173,0.12)",
          },
        }}
      >
        {combinedTimeline.map(({ type, title, time, by, comment, old_status, new_status, days_to_cure, notes }, index) => {
          const config        = timelineTypeConfig[type] || timelineTypeConfig.violation;
          const IconComponent = config.icon;

          return (
            <motion.div
              key={`${type}-${time}-${index}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, delay: index * 0.03 }}
            >
              <Box sx={{ position: "relative", mb: 1.25 }}>
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: "-15px", sm: "-18px" },
                    top: 18,
                    width: { xs: 14, sm: 16 },
                    height: { xs: 14, sm: 16 },
                    borderRadius: "999px",
                    backgroundColor: config.color,
                    border: "3px solid #fff",
                    boxShadow: "0 0 0 1px rgba(47,99,173,0.08)",
                    zIndex: 2,
                  }}
                />

                <Card
                  sx={{
                    borderRadius: "14px",
                    border: type === "status"
                      ? `1px solid ${config.color}30`
                      : "1px solid rgba(47,99,173,0.08)",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.04)",
                    backgroundColor: type === "status" ? `${config.color}06` : "#fff",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: { xs: 1.1, sm: 1.25 } }}>
                    {/* ── Header: icon + title + type chip ── */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.9, minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            width: { xs: 30, sm: 32 },
                            height: { xs: 30, sm: 32 },
                            borderRadius: "9px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: `${config.color}15`,
                            flexShrink: 0,
                          }}
                        >
                          <IconComponent sx={{ color: config.color, fontSize: 16 }} />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.25, wordBreak: "break-word", overflowWrap: "anywhere" }}
                          >
                            {type === "status"
                              ? "Status Change"
                              : type === "cure_reset"
                              ? title.includes("Started") ? "Cure Period Started" : "Cure Period Restarted"
                              : title}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, width: { xs: "100%", sm: "auto" }, pl: { xs: "38px", sm: 0 } }}>
                        <Chip
                          size="small"
                          label={config.label}
                          sx={{
                            maxWidth: "100%",
                            height: 24,
                            borderRadius: "999px",
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color: config.color,
                            backgroundColor: `${config.color}15`,
                            "& .MuiChip-label": { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", px: 1 },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* ── Status change: colored chips + arrow ── */}
                    {type === "status" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75, pl: "38px", flexWrap: "wrap" }}>
                        {old_status ? (
                          <Chip label={old_status} size="small" sx={statusChipSx(old_status)} />
                        ) : (
                          <Typography variant="caption" sx={{ color: "#9aa5b4", fontStyle: "italic" }}>Initial</Typography>
                        )}
                        <SwapHorizIcon sx={{ fontSize: 15, color: "#9aa5b4", flexShrink: 0 }} />
                        <Chip label={new_status || "N/A"} size="small" sx={statusChipSx(new_status)} />
                      </Box>
                    )}

                    {/* ── Cure reset details ── */}
                    {type === "cure_reset" && (
                      <Box sx={{ p: 1, borderRadius: "10px", backgroundColor: "#E3F2FD", border: "1px solid rgba(21,101,192,0.14)", mb: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#0D47A1", fontSize: "0.82rem" }}>
                          Days to Cure: <strong>{days_to_cure ?? "N/A"}</strong>
                        </Typography>
                        {notes && (
                          <Typography variant="body2" sx={{ mt: 0.45, fontStyle: "italic", color: "#1565C0", fontSize: "0.8rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                            Notes: "{notes}"
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* ── Status change optional comment ── */}
                    {type === "status" && comment && (
                      <Box sx={{ p: 1, borderRadius: "10px", backgroundColor: "#f9fafb", border: "1px solid rgba(47,99,173,0.06)", mb: 0.75, ml: "38px" }}>
                        <Typography variant="body2" sx={{ color: "#555", fontStyle: "italic", fontSize: "0.82rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                          "{comment}"
                        </Typography>
                      </Box>
                    )}

                    {/* ── Comment body ── */}
                    {type === "comment" && (
                      <Box sx={{ p: 1, borderRadius: "10px", backgroundColor: "#f9fafb", border: "1px solid rgba(47,99,173,0.06)", mb: 0.75 }}>
                        <Typography variant="body2" sx={{ color: "#555", fontStyle: "italic", fontSize: "0.82rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                          "{title}"
                        </Typography>
                      </Box>
                    )}

                    {/* ── Body text for non-special types ── */}
                    {type !== "status" && type !== "cure_reset" && type !== "comment" && (
                      <Typography variant="body2" sx={{ color: "#223142", mb: by || time ? 0.55 : 0, fontSize: "0.82rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {title}
                      </Typography>
                    )}

                    {/* ── By line ── */}
                    {by && (
                      <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.35, fontSize: "0.77rem", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {type === "violation"
                          ? "Created by "
                          : type === "email"
                          ? "To "
                          : type === "status"
                          ? "Changed by "
                          : "By "}
                        <strong>{by}</strong>
                      </Typography>
                    )}

                    {/* ── Timestamp ── */}
                    <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "#8a94a6", fontStyle: "italic", display: "flex", alignItems: "center", mt: 0.25, wordBreak: "break-word" }}>
                      <AccessTimeIcon sx={{ fontSize: 11, mr: 0.45 }} />
                      {new Date(time).toLocaleString()}
                    </Typography>
                  </Box>
                </Card>
              </Box>
            </motion.div>
          );
        })}
      </Box>

      <Legend />
    </Box>
  );
};

const Legend = () => {
  const items = ["violation", "status", "cure_reset", "comment", "event", "email"];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142", mb: 0.75 }}>
        Legend
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {items.map((key) => {
          const item          = timelineTypeConfig[key];
          const IconComponent = item.icon;
          return (
            <Chip
              key={key}
              icon={<IconComponent sx={{ fontSize: "14px !important" }} />}
              label={item.label}
              size="small"
              sx={{
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: "0.72rem",
                color: item.color,
                backgroundColor: `${item.color}15`,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ViolationsTimeline;
